import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Loader2, Trash2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BELogo } from "../components/BELogo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";

const STATUS_COLORS = {
  new: "bg-[#FF6A13]/10 text-[#FF6A13]",
  contacted: "bg-blue-500/10 text-blue-600",
  scheduled: "bg-purple-500/10 text-purple-600",
  closed: "bg-green-500/10 text-green-600",
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(a.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load", { description: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const list = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div className="min-h-screen bg-[#f5f5f7]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BELogo size={28} />
            <span className="text-xs tracking-[0.18em] uppercase font-bold">
              Dealer Portal
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#86868B] hidden sm:inline">{user.email}</span>
            <Button
              variant="ghost"
              onClick={logout}
              className="rounded-full text-[#1d1d1f]"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="h2-section text-3xl md:text-4xl">Demo Bookings</h1>
        <p className="text-[#86868B] mt-1">Manage and respond to customer demo requests.</p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { k: "Total", v: stats.total ?? 0, c: "bg-[#1d1d1f] text-white" },
            { k: "New", v: stats.new ?? 0, c: "bg-white border border-black/5" },
            { k: "Contacted", v: stats.contacted ?? 0, c: "bg-white border border-black/5" },
            { k: "Scheduled", v: stats.scheduled ?? 0, c: "bg-white border border-black/5" },
            { k: "Closed", v: stats.closed ?? 0, c: "bg-white border border-black/5" },
          ].map((s) => (
            <div
              key={s.k}
              className={`p-5 rounded-2xl ${s.c}`}
              data-testid={`stat-${s.k.toLowerCase()}`}
            >
              <div className="text-xs uppercase tracking-widest opacity-70">{s.k}</div>
              <div className="font-display text-3xl font-black mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="mt-8 flex items-center gap-2 flex-wrap">
          {["all", "new", "contacted", "scheduled", "closed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full font-semibold transition ${
                filter === f
                  ? "bg-[#FF6A13] text-white"
                  : "bg-white border border-black/5 text-[#1d1d1f]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-3xl border border-black/5 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="inline animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="p-16 text-center text-[#86868B]" data-testid="empty-bookings">
              No bookings yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Preferred</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((b) => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="text-xs text-[#86868B]">
                      {new Date(b.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-semibold">{b.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-xs">
                        <a href={`tel:${b.phone}`} className="flex items-center gap-1 hover:text-[#FF6A13]">
                          <Phone size={12} /> {b.phone}
                        </a>
                        {b.email && (
                          <a href={`mailto:${b.email}`} className="flex items-center gap-1 hover:text-[#FF6A13]">
                            <Mail size={12} /> {b.email}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-sm">
                        <MapPin size={12} className="text-[#FF6A13]" /> {b.location}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {b.model_name || <span className="text-[#86868B]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {b.preferred_date || <span className="text-[#86868B]">—</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                        <SelectTrigger
                          className={`h-8 text-xs rounded-full font-semibold border-0 w-[130px] ${STATUS_COLORS[b.status]}`}
                          data-testid={`status-select-${b.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">new</SelectItem>
                          <SelectItem value="contacted">contacted</SelectItem>
                          <SelectItem value="scheduled">scheduled</SelectItem>
                          <SelectItem value="closed">closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <a
                        href={`https://wa.me/${b.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#25D366] text-xs font-semibold hover:underline mr-3"
                        data-testid={`wa-${b.id}`}
                      >
                        WhatsApp
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(b.id)}
                        data-testid={`delete-${b.id}`}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
